import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { TrendingUp, Info } from 'lucide-react';
import type { FinancialTrendPoint } from '../../types/admin-partner';
import { formatBrl } from './ExecutiveMetricCard';
import {
  Tooltip as UiTooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface PartnerFinancialPerformanceChartProps {
  data?: FinancialTrendPoint[];
  isLoading?: boolean;
  onGroupByChange?: (groupBy: 'DAY' | 'MONTH') => void;
  currentGroupBy?: 'DAY' | 'MONTH';
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const pointData = payload[0]?.payload as FinancialTrendPoint;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg text-xs space-y-1.5 min-w-[180px]">
        <div className="font-semibold text-foreground border-b border-border/60 pb-1 flex justify-between items-center">
          <span>{label}</span>
          {pointData?.takeRatePercent !== undefined && (
            <span className="text-primary font-bold">
              Take-rate: {pointData.takeRatePercent}%
            </span>
          )}
        </div>
        <div className="flex justify-between items-center text-blue-600 dark:text-blue-400 font-medium">
          <span>GMV Total:</span>
          <span>{formatBrl(pointData?.gmv || 0)}</span>
        </div>
        <div className="flex justify-between items-center text-emerald-600 dark:text-emerald-400 font-medium">
          <span>Comissões Líquidas:</span>
          <span>{formatBrl(pointData?.netCommission || 0)}</span>
        </div>
        <div className="flex justify-between items-center text-amber-600 dark:text-amber-400 font-medium">
          <span>Comissões Pagas:</span>
          <span>{formatBrl(pointData?.paidCommission || 0)}</span>
        </div>
      </div>
    );
  }
  return null;
};

export const PartnerFinancialPerformanceChart: React.FC<PartnerFinancialPerformanceChartProps> = ({
  data = [],
  isLoading = false,
  onGroupByChange,
  currentGroupBy = 'DAY',
}) => {
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-xs animate-pulse space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-6 w-52 bg-muted rounded"></div>
          <div className="h-8 w-32 bg-muted rounded-lg"></div>
        </div>
        <div className="h-72 bg-muted/50 rounded-lg"></div>
      </div>
    );
  }

  // Agregações do período
  const totalGmv = data.reduce((acc, curr) => acc + (curr.gmv || 0), 0);
  const totalNet = data.reduce((acc, curr) => acc + (curr.netCommission || 0), 0);
  const totalPaid = data.reduce((acc, curr) => acc + (curr.paidCommission || 0), 0);
  const averageTakeRate = totalGmv > 0 ? Number(((totalNet / totalGmv) * 100).toFixed(2)) : 0;

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-xs flex flex-col justify-between">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
          <div>
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Desempenho Financeiro & Volume (GMV vs Comissões)
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Evolução da receita bruta intermediada e comissões provisionadas/pagas a parceiros
            </p>
          </div>

          <div className="flex items-center gap-2">
            {onGroupByChange && (
              <div className="bg-muted p-1 rounded-lg flex items-center gap-1 border border-border/50 text-xs">
                <button
                  type="button"
                  onClick={() => onGroupByChange('DAY')}
                  className={`px-2.5 py-1 rounded-md font-medium transition-all ${
                    currentGroupBy === 'DAY'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Diário
                </button>
                <button
                  type="button"
                  onClick={() => onGroupByChange('MONTH')}
                  className={`px-2.5 py-1 rounded-md font-medium transition-all ${
                    currentGroupBy === 'MONTH'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Mensal
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mini stats da série */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-muted-foreground block truncate">
                GMV Acumulado
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Volume total de consultas intermediadas por parceiros no período selecionado.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-base font-bold text-blue-600 dark:text-blue-400">
              {formatBrl(totalGmv)}
            </span>
          </div>

          <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-muted-foreground block truncate">
                Comissões Líquidas
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Valor total de comissões aprovadas para os parceiros da plataforma.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
              {formatBrl(totalNet)}
            </span>
          </div>

          <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-muted-foreground block truncate">
                Comissões Pagas
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Valor de comissões que já foram transferidas via PIX para os parceiros.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-base font-bold text-amber-600 dark:text-amber-400">
              {formatBrl(totalPaid)}
            </span>
          </div>

          <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-muted-foreground block truncate">
                Take-rate Médio
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Percentual médio do valor das consultas que é repassado como comissão aos afiliados.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-base font-bold text-primary">
              {averageTakeRate}%
            </span>
          </div>
        </div>

        {/* Gráfico Recharts com gradientes e áreas sobrepostas */}
        <div className="h-72 w-full mt-2">
          {data.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
              Nenhuma movimentação registrada no período selecionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gmvGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="netCommGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="paidCommGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/60" />

                <XAxis
                  dataKey="label"
                  stroke="currentColor"
                  className="text-[11px] text-muted-foreground"
                  tickLine={false}
                  axisLine={{ stroke: 'currentColor', opacity: 0.2 }}
                />

                <YAxis
                  stroke="currentColor"
                  className="text-[11px] text-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => `R$${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                />

                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: '12px', fontSize: '12px' }}
                />

                <Area
                  type="monotone"
                  dataKey="gmv"
                  name="GMV (Consultas)"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#gmvGradient)"
                />

                <Area
                  type="monotone"
                  dataKey="netCommission"
                  name="Comissões Aprovadas"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#netCommGradient)"
                />

                <Area
                  type="monotone"
                  dataKey="paidCommission"
                  name="Comissões Pagas"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fillOpacity={1}
                  fill="url(#paidCommGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
        <span>* Valores calculados com base nas consultas vinculadas a afiliados</span>
        <span className="font-medium text-foreground">
          Take-rate de {averageTakeRate}% sobre o GMV
        </span>
      </div>
    </div>
  );
};
